'use client'

import { useEffect, useRef } from 'react'
import { canonicalBrowserSearch } from './browser-location'
import {
  type ClientJourney,
  isClientMetricsEnabled,
  startClientJourney,
} from './metrics-browser'

export type PageReadinessState =
  | 'loading'
  | 'ready'
  | 'empty'
  | 'error'
  | 'partial'
  | 'unavailable'

type Contract = {
  domain: string
  phase: string
  /** Every declared part must commit a terminal state before success. */
  parts?: ReadonlyArray<string>
  matches: (pathname: string) => boolean
  /** Ephemeral UI intent that does not change the page's content gate. */
  ignoredSearchKeys?: ReadonlyArray<string>
}
type Location = { pathname: string; search: string }
type Attachment<Surface extends string> = Location & {
  surface: Surface
  part?: string
  /** Local scope only; never exported as a tag or context. */
  scope?: string
}
type Lease = {
  update: (state: PageReadinessState, completedAt?: number) => void
  release: () => void
}
export type PageReadinessTelemetry<Surface extends string> = {
  begin: (url: string, navigationType: string) => void
  beginInitial: () => void
  attach: (options: Attachment<Surface>) => Lease
  fail: (pathname?: string) => void
  /** Close an expected fallback only for the current committed URL; never start a journey. */
  unavailable: () => void
}
const noopLease: Lease = { update() {}, release() {} }
const terminal = {
  ready: ['success', 'content_ready'],
  empty: ['success', 'empty_state'],
  error: ['failure', 'content_error'],
  partial: ['partial', 'partial_content'],
  unavailable: ['skipped', 'unavailable_state'],
} as const

/**
 * One foreground page per app. Contracts name concrete usable-content gates,
 * never "every query settled". URL/query/scope values are retained locally.
 * Initial-document timing begins at browser bootstrap; a missing navigation
 * start falls back to committed mount and carries a distinct trigger tag.
 */
export function createPageReadinessTelemetry<Surface extends string>(
  contracts: Readonly<Record<Surface, Contract>>,
  measurement: {
    operation: string
    readyReason: string
    useAttachedSurface?: boolean
  } = { operation: 'client.page.ready', readyReason: 'content_ready' }
): PageReadinessTelemetry<Surface> {
  const surfaces = Object.keys(contracts) as Array<Surface>
  const surfaceFor = (location: Location) =>
    surfaces.find((surface) => contracts[surface].matches(location.pathname))
  const locationKey = (location: Location): string => {
    const surface = surfaceFor(location)
    return `${location.pathname}?${canonicalBrowserSearch(location.search, surface ? contracts[surface].ignoredSearchKeys : undefined)}`
  }
  type Entry = Attachment<Surface> & {
    journey: ClientJourney
    owner?: symbol
    settled: boolean
    owners: Map<string, symbol>
    states: Map<string, PageReadinessState>
    fromKey?: string
  }
  let active: Entry | null = null
  const matchesCommittedLocation = (entry: Entry): boolean =>
    locationKey(entry) === locationKey(window.location)
  const finish = (
    entry: Entry,
    state: Exclude<PageReadinessState, 'loading'>,
    completedAt?: number
  ) => {
    if (entry.settled) {
      return
    }
    entry.settled = true
    const [outcome, reason] = terminal[state]
    const terminalReason = state === 'ready' ? measurement.readyReason : reason
    entry.journey.finish(outcome, {
      ...(completedAt !== undefined ? { completedAt } : {}),
      tags: {
        reason: terminalReason,
        ...(measurement.useAttachedSurface
          ? {
              surface: entry.surface,
              domain: contracts[entry.surface].domain,
              phase: contracts[entry.surface].phase,
            }
          : {}),
      },
    })
  }
  const cancel = (reason: string) => {
    if (active && !active.settled) {
      active.settled = true
      active.journey.finish('cancelled', { tags: { reason } })
    }
    active = null
  }
  const start = (
    options: Attachment<Surface>,
    trigger: 'navigation' | 'initial_document' | 'committed_mount',
    navigationType = 'other'
  ): Entry => {
    const { domain, phase } = contracts[options.surface]
    active = {
      ...options,
      settled: false,
      owners: new Map(),
      states: new Map(),
      journey: startClientJourney(measurement.operation, {
        timeoutMs: 60_000,
        tags: {
          domain,
          phase,
          surface: options.surface,
          trigger,
          navigation_type: ['push', 'replace', 'traverse'].includes(
            navigationType
          )
            ? navigationType
            : 'other',
        },
      }),
    }
    return active
  }
  const startLocation = (
    location: Location,
    trigger: 'navigation' | 'initial_document',
    navigationType?: string,
    fromKey?: string
  ) => {
    const surface = surfaceFor(location)
    if (surface) {
      const entry = start(
        { pathname: location.pathname, search: location.search, surface },
        trigger,
        navigationType
      )
      entry.fromKey = fromKey
    }
  }
  return {
    begin(url, navigationType) {
      if (!isClientMetricsEnabled()) {
        return
      }
      try {
        const current = window.location
        const destination = new URL(url, current.href)
        if (destination.origin !== current.origin) {
          return
        }
        const currentKey = locationKey(current)
        if (locationKey(destination) === currentKey) {
          // A same-URL router action supersedes another destination, but must
          // preserve an unfinished content gate for the currently visible page.
          if (active && locationKey(active) !== currentKey) {
            cancel('superseded')
          }
          return
        }
        cancel('superseded')
        startLocation(destination, 'navigation', navigationType, currentKey)
      } catch {
        // Instrumentation must not change routing behavior.
      }
    },
    beginInitial() {
      if (!isClientMetricsEnabled()) {
        return
      }
      try {
        if (!active) {
          startLocation(window.location, 'initial_document')
        }
      } catch {
        // Browser bootstrap may run without a usable location.
      }
    },
    attach(options) {
      if (!isClientMetricsEnabled()) {
        return noopLease
      }
      try {
        if (!contracts[options.surface]?.matches(options.pathname)) {
          return noopLease
        }
        const part = options.part ?? 'foreground'
        const parts = contracts[options.surface].parts ?? ['foreground']
        if (!parts.includes(part)) {
          return noopLease
        }
        // An outgoing tree must not complete a newly requested destination.
        if (active && locationKey(active) !== locationKey(options)) {
          if (!active.owner) {
            const committed = locationKey(options)
            if (
              committed !== locationKey(window.location) ||
              committed === active.fromKey
            ) {
              return noopLease
            }
            cancel('redirected')
          } else {
            cancel('route_changed')
          }
        }
        if (active?.owner && active.scope !== options.scope) {
          cancel('scope_changed')
        }
        const entry = active ?? start(options, 'committed_mount')
        if (measurement.useAttachedSurface) {
          entry.surface = options.surface
        }
        const owner = Symbol('page-readiness-owner')
        entry.owner = owner
        entry.owners.set(part, owner)
        entry.states.delete(part)
        entry.scope = options.scope
        return {
          update(state, completedAt) {
            if (active !== entry || entry.owners.get(part) !== owner) {
              return
            }
            entry.states.set(part, state)
            if (state === 'loading') {
              return
            }
            if (state === 'error' || state === 'unavailable') {
              finish(entry, state, completedAt)
              return
            }
            if (
              !parts.every(
                (required) =>
                  entry.states.has(required) &&
                  entry.states.get(required) !== 'loading'
              )
            ) {
              return
            }
            const values = parts.map((required) => entry.states.get(required))
            let combined: 'ready' | 'empty' | 'partial' = 'ready'
            if (values.every((value) => value === 'empty')) {
              combined = 'empty'
            }
            if (values.includes('partial')) {
              combined = 'partial'
            }
            finish(entry, combined, completedAt)
          },
          release() {
            // Let a fallback committed in the same React pass report failure,
            // and Strict Mode reattachment replace the owner, before cancellation.
            try {
              queueMicrotask(() => {
                if (active === entry && entry.owners.get(part) === owner) {
                  cancel('unmounted')
                }
              })
            } catch {
              if (active === entry && entry.owners.get(part) === owner) {
                cancel('unmounted')
              }
            }
          },
        }
      } catch {
        return noopLease
      }
    },
    fail(pathname) {
      try {
        const currentPath = pathname ?? window.location.pathname
        if (
          active?.pathname === currentPath &&
          matchesCommittedLocation(active)
        ) {
          finish(active, 'error')
        }
      } catch {
        // The existing error reporter owns errors; this only closes a duration.
      }
    },
    unavailable() {
      try {
        if (active && !active.settled && matchesCommittedLocation(active)) {
          finish(active, 'unavailable')
        }
      } catch {
        // An expected missing resource must not change fallback rendering.
      }
    },
  }
}

/** Passive effects observe committed UI without adding a frame loop or render work. */
export function usePageReadinessTelemetry<Surface extends string>(
  telemetry: PageReadinessTelemetry<Surface>,
  options: Attachment<Surface> & {
    state: PageReadinessState
    enabled?: boolean
  }
): void {
  const {
    surface,
    part,
    pathname,
    search,
    scope,
    state,
    enabled = true,
  } = options
  const lease = useRef<Lease>(noopLease)
  useEffect(() => {
    const attached = enabled
      ? telemetry.attach({ surface, part, pathname, search, scope })
      : noopLease
    lease.current = attached
    return attached.release
  }, [telemetry, surface, part, pathname, search, scope, enabled])
  // Also announce unchanged state after a route/scope reattachment.
  useEffect(() => lease.current.update(state))
}
